/**
 * Client-side cast prediction (visuals only; the sim stays the authority).
 *
 * On a key press the client asks the sim's own `canCast` about the current state. If the
 * press will start (or queue) a cast, the HUD and renderer show it at once: button pressed,
 * GCD sweep, cast bar, cast pose / charge-up. When the sim step that applied the command
 * runs, `reconcile` confirms the prediction (sim state takes over) or rejects it (visuals are
 * cancelled and the caller shows the error).
 *
 * Transforms and instant procs (rotation rework F2): the client predicts what the press casts
 * NOW. `pressFormId` resolves the bound (pressed) id through the unit's override map, and the
 * session times that form with the sim's own `castTiming`, which already reads the player's
 * auras (Lava Surge's castTimePct −1 makes a Lava Burst press start as an instant, with no bar).
 * `Prediction.abilityId` is the form (it is what begincast / cast / unit.cast carry);
 * `pressedId` is the bound id (what the command and the spell queue carry).
 */
import type { CastResult, ContentRegistry, DeepReadonly, SimEvent, UnitId, World } from '@keystone/sim';
import { SPELL_QUEUE_WINDOW_MS, TICK_MS, resolveAbilityId } from '@keystone/sim';

/** The ability a press of `pressedId` casts right now for `actorId` (its transformed form, or itself). */
export function pressFormId(content: Pick<ContentRegistry, 'ability'>, world: DeepReadonly<World>, actorId: UnitId, pressedId: string): string {
  return resolveAbilityId(content, world, actorId, pressedId);
}

export interface PredictedCast {
  abilityId: string;
  kind: 'cast' | 'channel';
  /** Sim-time ms (fractional: the sim's "present", see FixedStepAccumulator.sinceStepMs). */
  startedAt: number;
  endsAt: number;
  targetId: UnitId | null;
  interruptible: boolean;
}

export interface Prediction {
  /** The ability that will run (the transformed form when the button is transformed). */
  abilityId: string;
  /** The bound id the key sent, when it differs from `abilityId` (spell queue entries carry it). */
  pressedId?: string;
  actorId: UnitId;
  /** `start`: the press starts the ability; `queued`: it goes into the 400 ms spell queue. */
  state: 'start' | 'queued';
  /** Predicted GCD (sim-time ms), null for off-GCD abilities and queued presses. */
  gcd: { start: number; until: number } | null;
  /** Predicted cast bar, null for instants and queued presses. */
  cast: PredictedCast | null;
  /** Instant: the client already played the cast animation cue (the sim's cue is skipped). */
  cued: boolean;
  /** Real time of the press (stale predictions expire). */
  pressedAt: number;
  /** The command object sent to the sim (identifies the step that applied it). */
  command: unknown;
}

export interface PressInput {
  world: DeepReadonly<World>;
  actorId: UnitId;
  /** The ability that will run (resolve the pressed id with `pressFormId`). */
  abilityId: string;
  /** The bound id the key sent (defaults to `abilityId`). */
  pressedId?: string;
  targetId: UnitId | null;
  /** `canCast` on the current state (the sim's own query). */
  check: CastResult;
  /** For a busy result (GCD / casting / cooldown): ms left on what blocks it, at `world.time`. */
  busyLeft: number;
  /** `castTiming` for the actor (exact sim math). */
  timing: { gcdMs: number; castMs: number; kind: 'instant' | 'cast' | 'channel' };
  interruptible: boolean;
  /** Sim-time "present" (world.time + time since the latest step; may be < world.time). */
  presentMs: number;
  realNow: number;
  command: unknown;
}

const BUSY_REASONS = new Set(['on_gcd', 'casting', 'on_cooldown']);

/** Predict what a press does. Null = nothing to predict (it will fail; the caller shows the error). */
export function predictPress(p: PressInput): Prediction | null {
  const u = p.world.units[p.actorId];
  if (!u || !u.alive) return null;
  const base: Pick<Prediction, 'abilityId' | 'pressedId' | 'actorId' | 'cued' | 'pressedAt' | 'command'> = {
    abilityId: p.abilityId,
    actorId: p.actorId,
    cued: false,
    pressedAt: p.realNow,
    command: p.command,
  };
  if (p.pressedId !== undefined && p.pressedId !== p.abilityId) base.pressedId = p.pressedId;
  let start = p.check.ok;
  if (!p.check.ok) {
    if (!BUSY_REASONS.has(p.check.reason)) return null;
    // The command is applied on the next tick, so judge the queue window there.
    const leftThen = p.busyLeft - TICK_MS;
    if (leftThen > SPELL_QUEUE_WINDOW_MS) return null; // refused (the sim drops it)
    if (leftThen > 0) return { ...base, state: 'queued', gcd: null, cast: null };
    start = true; // free again by the time it is applied
  }
  if (!start) return null;
  const now = p.presentMs;
  const gcd = p.timing.gcdMs > 0 ? { start: now, until: now + p.timing.gcdMs } : null;
  const hasBar = p.timing.kind === 'channel' || p.timing.castMs > 0;
  const cast: PredictedCast | null = hasBar
    ? { abilityId: p.abilityId, kind: p.timing.kind === 'channel' ? 'channel' : 'cast', startedAt: now, endsAt: now + p.timing.castMs, targetId: p.targetId, interruptible: p.interruptible }
    : null;
  return { ...base, state: 'start', gcd, cast };
}

export type Reconcile = { status: 'confirmed' } | { status: 'rejected' } | { status: 'pending' };

/**
 * After the sim step that applied `p.command`: did the sim start / queue the ability?
 * Confirmed if the step emitted its begincast/cast, or the unit is now casting or queueing it.
 */
export function reconcile(p: Prediction, world: DeepReadonly<World>, events: readonly SimEvent[], applied: boolean): Reconcile {
  if (!applied) return { status: 'pending' };
  const u = world.units[p.actorId];
  const fired = events.some((e) => (e.type === 'begincast' || e.type === 'cast') && e.sourceID === p.actorId && e.abilityGameID === p.abilityId);
  // The spell queue keeps the pressed id (re-resolved when it fires).
  const queued = u?.queued?.abilityId === (p.pressedId ?? p.abilityId) || u?.queued?.abilityId === p.abilityId;
  if (fired || u?.cast?.abilityId === p.abilityId || queued) return { status: 'confirmed' };
  return { status: 'rejected' };
}

/** Predictions whose command was never applied (paused, disposed) expire after this long. */
export const PREDICTION_TTL_MS = 600;

/**
 * Live local feedback shared with the HUD widgets (action bar, cast bars), which cannot take
 * new constructor arguments. The session owns it: it writes the clock and prediction each
 * frame and resets it on dispose. Widgets fall back to plain sim state when it is empty.
 */
export interface LocalFeedback {
  /** The human's unit (null = spectating / no session). */
  actorId: UnitId | null;
  /** Sim-time "present" for smooth sweeps and bars (null = use world.time). */
  presentMs: number | null;
  prediction: Prediction | null;
}

export const localFeedback: LocalFeedback = { actorId: null, presentMs: null, prediction: null };

export function resetLocalFeedback(): void {
  localFeedback.actorId = null;
  localFeedback.presentMs = null;
  localFeedback.prediction = null;
}

/** The sim-time clock HUD widgets should use with this world. */
export function hudNow(world: DeepReadonly<World>): number {
  return localFeedback.presentMs ?? world.time;
}

/** The cast the HUD / renderer should show for `unitId`: the sim's, else a predicted one. */
export function shownCast(world: DeepReadonly<World>, unitId: UnitId): PredictedCast | null {
  const u = world.units[unitId];
  if (!u?.alive) return null;
  if (u.cast) return { abilityId: u.cast.abilityId, kind: u.cast.kind, startedAt: u.cast.startedAt, endsAt: u.cast.endsAt, targetId: u.cast.targetId, interruptible: u.cast.interruptible };
  const p = localFeedback.prediction;
  return p && p.actorId === unitId && p.cast ? p.cast : null;
}
